/**
 * The editor behind the Clinic roles, Disciplines and Employee type cells:
 * one row per clinic with all three, for when they differ clinic by clinic.
 * The cells handle the case where every clinic agrees. Only clinics the
 * member has access to, within the operator's bound, are listed. Every change
 * saves at once through the grid, like the cells; there is nothing to
 * confirm. The rows read the grid's member, so a save that fails and is put
 * back shows here too.
 */

import React, { useState } from 'react';
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ChevronDown } from 'lucide-react';
import type { DisciplineType } from '../../../services/api/models/DisciplineType';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import { DISCIPLINE_ORDER } from '../../../constants/disciplines';
import { EMPLOYEE_TYPE_OPTIONS } from '../../../constants/employeeTypes';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { ChecklistPopover, useChecklist } from '../../spreadsheet/checklist';
import type { OrgMemberResponse } from '../types';
import { FieldLabel } from '../UserLicensesSection';
import {
  type ClinicRoleIds,
  type ClinicRoles,
  type ClinicSetupEntry,
  DISCIPLINE_OPTIONS,
  type Employment,
  employmentOf,
  memberDisplayName,
  needsOtherText,
  withDisciplines,
  withEmployment,
  withRoles,
} from './clinicSetup';

const NONE_VALUE = '__none__';

/** A disciplines checklist opened from a field-shaped button. */
const DisciplinesDropdown: React.FC<{
  value: DisciplineType[];
  disabled: boolean;
  placeholder: string;
  ariaLabel: string;
  onChange: (value: DisciplineType[]) => void;
}> = ({ value, disabled, placeholder, ariaLabel, onChange }) => {
  const checklist = useChecklist({
    options: DISCIPLINE_OPTIONS,
    value,
    onChange: values => onChange(values as DisciplineType[]),
    disabled,
  });
  const text =
    value.length === DISCIPLINE_ORDER.length
      ? 'All disciplines'
      : DISCIPLINE_ORDER.filter(d => value.includes(d)).join(', ');
  return (
    <ChecklistPopover
      checklist={checklist}
      trigger={
        <button
          ref={checklist.triggerRef}
          type='button'
          role='combobox'
          aria-expanded={checklist.open}
          aria-label={text ? `${ariaLabel}: ${text}` : ariaLabel}
          disabled={disabled}
          className='flex h-8 w-full items-center gap-2 rounded-md border border-input bg-background px-2 text-left text-sm disabled:cursor-default disabled:opacity-50'
        >
          <span
            className={`min-w-0 flex-1 truncate ${text ? '' : 'text-muted-foreground'}`}
          >
            {text || placeholder}
          </span>
          <ChevronDown className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
        </button>
      }
      contentClassName='w-auto min-w-[var(--radix-popover-trigger-width)] max-w-xs rounded-sm p-0.5'
      sideOffset={4}
      ariaLabel={ariaLabel}
      variant='cell'
    />
  );
};

const ROLE_COLUMNS: { key: keyof ClinicRoles; label: string }[] = [
  { key: 'caseManager', label: 'Case Manager' },
  { key: 'clinicalManager', label: 'Clinical Manager' },
  { key: 'clinician', label: 'Clinician' },
];

// Four cells: Case Manager, Clinical Manager, Clinician, its disciplines. The
// disciplines dropdown is always there, greyed until they are a clinician, so
// the column never jumps. Stacked (a phone), each control carries its label.
const AssignableControl: React.FC<{
  entry: ClinicSetupEntry;
  clinic: string;
  /** Null until the role catalogue has loaded: the roles cannot change yet. */
  onRoles: ((roles: ClinicRoles) => void) | null;
  heldThrough: (key: keyof ClinicRoles) => string | null;
  onDisciplines: (disciplines: DisciplineType[]) => void;
  stacked?: boolean;
}> = ({ entry, clinic, onRoles, heldThrough, onDisciplines, stacked }) => {
  const roles = ROLE_COLUMNS.map(({ key, label }) => {
    // Held through another role: ticked, and not the grid's to change.
    const via = heldThrough(key);
    const Wrapper = stacked ? 'label' : 'div';
    return (
      <Wrapper
        key={key}
        className={
          stacked ? 'flex items-center gap-2 text-sm' : 'flex justify-center'
        }
        title={via ? `${via} at ${clinic}` : undefined}
      >
        <Checkbox
          aria-label={`${label} at ${clinic}`}
          checked={entry[key]}
          disabled={!onRoles || !!via}
          onCheckedChange={() =>
            onRoles?.({
              clinician: entry.clinician,
              caseManager: entry.caseManager,
              clinicalManager: entry.clinicalManager,
              [key]: !entry[key],
            })
          }
        />
        {stacked && label}
      </Wrapper>
    );
  });
  const disciplines = (
    <DisciplinesDropdown
      value={entry.disciplines}
      placeholder={entry.clinician ? 'No disciplines' : '—'}
      ariaLabel={`Disciplines at ${clinic}`}
      disabled={!entry.clinician}
      onChange={onDisciplines}
    />
  );
  if (!stacked) {
    return (
      <>
        {roles}
        {disciplines}
      </>
    );
  }
  return (
    <>
      <div className='flex flex-wrap gap-x-4 gap-y-2'>{roles}</div>
      <div className='flex flex-col gap-1'>
        <FieldLabel>Disciplines</FieldLabel>
        {disciplines}
      </div>
    </>
  );
};

// The Select saves as picked. "Other" needs saying what, so its text is
// typed first and saved when the field is left (or the editor closed).
const EmploymentControl: React.FC<{
  value: Employment;
  clinic: string;
  onChange: (next: Employment) => void;
  onType: (next: Employment) => void;
  /** Focus the "Other" text when it appears: one row only. */
  autoFocus: boolean;
}> = ({ value, clinic, onChange, onType, autoFocus }) => (
  <div className='flex items-center gap-2'>
    <Select
      value={value.employee_type ?? NONE_VALUE}
      onValueChange={next => {
        const employment = withEmployment(
          value,
          next === NONE_VALUE ? null : (next as EmployeeType)
        );
        if (
          needsOtherText(employment.employee_type) &&
          !employment.employee_type_other?.trim()
        ) {
          onType(employment);
        } else {
          onChange(employment);
        }
      }}
    >
      <SelectTrigger
        selectSize='sm'
        className='min-w-0 flex-1'
        aria-label={`Employee type at ${clinic}`}
      >
        <SelectValue placeholder='Not set' />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_VALUE}>Not set</SelectItem>
        {EMPLOYEE_TYPE_OPTIONS.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {needsOtherText(value.employee_type) && (
      <Input
        inputSize='sm'
        className='min-w-0 flex-1'
        placeholder='Which type?'
        aria-label={`Other employee type at ${clinic}`}
        value={value.employee_type_other ?? ''}
        autoFocus={autoFocus}
        onChange={e =>
          onType({ ...value, employee_type_other: e.target.value })
        }
        onBlur={() => {
          if (value.employee_type_other?.trim()) onChange(value);
        }}
      />
    )}
  </div>
);

const HEADERS = [
  'Case Manager',
  'Clinical Manager',
  'Clinician',
  'Disciplines',
  'Employee type',
];

/** Clinic roles, disciplines and employee type, clinic by clinic. */
export const ClinicSetupDialog: React.FC<{
  member: OrgMemberResponse;
  /** The member's clinics as the grid shows them now. */
  entries: ClinicSetupEntry[];
  /**
   * An employee type picked for every clinic from the grid ("Other"): its text
   * is typed once, in any row, and saved at every clinic.
   */
  initialEmployment?: Employment;
  clinicName: (id: string) => string;
  roleIds: ClinicRoleIds | null;
  /** False without the clinic-info flag: employee type only. */
  showClinicRoles: boolean;
  /** The role a flag is held through when not the grid's own. */
  heldThrough: (
    entry: ClinicSetupEntry,
    key: keyof ClinicRoles
  ) => string | null;
  /** Saves the member's whole setup with one clinic changed. */
  onSave: (entries: ClinicSetupEntry[]) => void;
  onClose: () => void;
  /** Where focus goes when it closes (the grid, so its keys keep working). */
  onCloseAutoFocus?: (event: Event) => void;
}> = ({
  member,
  entries,
  initialEmployment,
  clinicName,
  roleIds,
  showClinicRoles,
  heldThrough,
  onSave,
  onClose,
  onCloseAutoFocus,
}) => {
  const headers = showClinicRoles ? HEADERS : ['Employee type'];
  const isMobile = useIsMobile();
  // "Other" while its text is being typed, before it is saved.
  const [drafts, setDrafts] = useState<ReadonlyMap<string, Employment>>(
    () =>
      new Map(
        initialEmployment
          ? entries.map(e => [e.clinic_id, initialEmployment])
          : []
      )
  );
  // Opened with a type for every clinic: the text typed in one row fills
  // them all, until the first save or another per-clinic change.
  const [together, setTogether] = useState(!!initialEmployment);
  const setDraft = (clinicId: string, value: Employment | null) =>
    setDrafts(prev => {
      const next = new Map(prev);
      if (value) next.set(clinicId, employmentOf(value));
      else next.delete(clinicId);
      return next;
    });
  const saveOne = (next: ClinicSetupEntry) =>
    onSave(entries.map(e => (e.clinic_id === next.clinic_id ? next : e)));
  const assignable = (
    entry: ClinicSetupEntry,
    clinic: string,
    stacked = false
  ) => (
    <AssignableControl
      entry={entry}
      clinic={clinic}
      onRoles={
        roleIds ? roles => saveOne(withRoles(entry, roles, roleIds)) : null
      }
      heldThrough={key => heldThrough(entry, key)}
      onDisciplines={disciplines =>
        saveOne(withDisciplines(entry, disciplines))
      }
      stacked={stacked}
    />
  );
  const employment = (entry: ClinicSetupEntry, clinic: string) => (
    <EmploymentControl
      value={drafts.get(entry.clinic_id) ?? entry}
      clinic={clinic}
      autoFocus={entry === entries[0]}
      onChange={next => {
        if (together && needsOtherText(next.employee_type)) {
          setDrafts(new Map());
          setTogether(false);
          onSave(entries.map(e => ({ ...e, ...employmentOf(next) })));
          return;
        }
        setTogether(false);
        setDraft(entry.clinic_id, null);
        saveOne({ ...entry, ...employmentOf(next) });
      }}
      onType={next =>
        together
          ? setDrafts(
              new Map(entries.map(e => [e.clinic_id, employmentOf(next)]))
            )
          : setDraft(entry.clinic_id, next)
      }
    />
  );
  // Closing keeps "Other" text that was typed but not yet saved, however the
  // editor is closed.
  const close = () => {
    const typed = entries.map(entry => {
      const draft = drafts.get(entry.clinic_id);
      return draft?.employee_type_other?.trim()
        ? { ...entry, ...employmentOf(draft) }
        : entry;
    });
    if (typed.some((entry, i) => entry !== entries[i])) onSave(typed);
    onClose();
  };
  return (
    <Dialog open onOpenChange={open => !open && close()}>
      <DialogContent
        className='sm:max-w-4xl'
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{memberDisplayName(member)}</DialogTitle>
          <DialogDescription>
            {showClinicRoles
              ? 'Clinic roles, disciplines and employee type at each clinic.'
              : 'Employee type at each clinic.'}{' '}
            Changes save as you make them.
            {together &&
              ' Type the "Other" employee type once for every clinic.'}
          </DialogDescription>
        </DialogHeader>
        {isMobile ? (
          // A phone has no room for the table: each clinic stacks its own
          // labelled controls, under its name in full.
          <div className='-mx-6 max-h-[60vh] divide-y overflow-y-auto border-y px-6'>
            {entries.map(entry => {
              const clinic = clinicName(entry.clinic_id);
              return (
                <div key={entry.clinic_id} className='space-y-3 py-3'>
                  <p className='text-sm font-medium'>{clinic}</p>
                  {showClinicRoles && assignable(entry, clinic, true)}
                  <div className='flex flex-col gap-1'>
                    <FieldLabel>Employee type</FieldLabel>
                    {employment(entry, clinic)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* One row per clinic. Each control fills its own column, so the
             rows line up whatever is ticked. */
          <div className='overflow-x-auto'>
            <div
              className={`grid items-center gap-x-4 gap-y-1 ${
                showClinicRoles
                  ? 'min-w-[44rem] grid-cols-[minmax(0,1fr)_5.5rem_6.5rem_4.5rem_10rem_12rem]'
                  : 'grid-cols-[minmax(0,1fr)_12rem]'
              }`}
            >
              <FieldLabel>Clinic</FieldLabel>
              {headers.map(h => (
                <FieldLabel
                  key={h}
                  className={
                    h === 'Disciplines' || h === 'Employee type'
                      ? ''
                      : 'text-center'
                  }
                >
                  {h}
                </FieldLabel>
              ))}
              {entries.map(entry => {
                const clinic = clinicName(entry.clinic_id);
                return (
                  <React.Fragment key={entry.clinic_id}>
                    <span
                      className='flex min-w-0 items-center gap-2 py-1 text-sm'
                      title={clinic}
                    >
                      <span className='truncate'>{clinic}</span>
                    </span>
                    {showClinicRoles && assignable(entry, clinic)}
                    {employment(entry, clinic)}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
